// Cookie consent, shared with the website (seventide.app) through one first-party cookie on the parent domain, so
// people choose once for both. Only advertising measurement needs consent: nothing else in Seventide sets
// non-essential cookies (see the cookie policy). The same cookie name and format are used by the website banner
// in website/parts/footer.html; change both together.

export type AdsConsent = 'granted' | 'denied';

export const CONSENT_COOKIE = 'st_consent';
/** Ask again after about 6 months. */
const MAX_AGE_SECONDS = 182 * 86_400;
const CHANGE_EVENT = 'st-consent-change';
const OPEN_EVENT = 'st-consent-open';

/** `.seventide.app` on the live site (so the website and the app share it); host-only elsewhere. */
function cookieDomain(host: string): string | null {
  return host === 'seventide.app' || host.endsWith('.seventide.app') ? '.seventide.app' : null;
}

export function readAdsConsent(cookie: string = document.cookie): AdsConsent | null {
  const raw = cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${CONSENT_COOKIE}=`))
    ?.slice(CONSENT_COOKIE.length + 1);
  if (raw === 'ads:granted') return 'granted';
  if (raw === 'ads:denied') return 'denied';
  return null;
}

export function writeAdsConsent(value: AdsConsent): void {
  const domain = cookieDomain(window.location.hostname);
  const parts = [
    `${CONSENT_COOKIE}=ads:${value}`,
    'Path=/',
    `Max-Age=${MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ];
  if (domain) parts.push(`Domain=${domain}`);
  if (window.location.protocol === 'https:') parts.push('Secure');
  document.cookie = parts.join('; ');
  window.dispatchEvent(new CustomEvent<AdsConsent>(CHANGE_EVENT, { detail: value }));
}

export function onAdsConsentChange(listener: (value: AdsConsent) => void): () => void {
  const handler = (e: Event) => listener((e as CustomEvent<AdsConsent>).detail);
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

/** Re-opens the cookie banner ("Cookie settings" links). */
export function openCookieSettings(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function onCookieSettingsOpen(listener: () => void): () => void {
  window.addEventListener(OPEN_EVENT, listener);
  return () => window.removeEventListener(OPEN_EVENT, listener);
}
